import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasChunkError: boolean;
}

class ChunkLoadErrorBoundary extends React.Component<Props, State> {
  private onlineHandler: (() => void) | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasChunkError: false };
  }

  private static readonly CHUNK_ERROR_PATTERNS = [
    'loading chunk',                          // Webpack (all browsers)
    'failed to fetch dynamically imported module', // Vite — Chrome/Edge
    'importing a module script failed',       // Vite — Safari
    'error loading dynamically imported module', // Vite — Firefox
    'dynamically imported module',            // Catch-all for Vite variations
    'failed to load module script',           // Alternate Safari wording
    'load failed',                            // Safari generic network failure for scripts
  ];

  private static isChunkLoadError(error: Error): boolean {
    if (error.name === 'ChunkLoadError') return true;

    const message = (error.message || '').toLowerCase();

    return ChunkLoadErrorBoundary.CHUNK_ERROR_PATTERNS.some((pattern) =>
      message.includes(pattern)
    );
  }

  static getDerivedStateFromError(error: Error): State | null {
    if (ChunkLoadErrorBoundary.isChunkLoadError(error)) {
      return { hasChunkError: true };
    }
    return null;
  }

  componentDidUpdate(_prevProps: Props, prevState: State): void {
    if (!this.state.hasChunkError || prevState.hasChunkError) return;

    if (navigator.onLine) {
      window.location.reload();
    } else {
      this.onlineHandler = () => window.location.reload();
      window.addEventListener('online', this.onlineHandler);
    }
  }

  componentWillUnmount(): void {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
  }

  render(): React.ReactNode {
    if (this.state.hasChunkError) {
      return <></>;
    }
    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;
